import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import axios from 'axios';
import {Settings, Zap, Edit, Trash2, Copy, Play, Pause, CheckCircle, XCircle} from 'lucide-react';
import InfoTooltip from '../common/InfoTooltip';
import CustomSelect from '../common/CustomSelect';
import ConfirmDialog from '../common/ConfirmDialog';
import HedgeWizard from './HedgeWizard';
import './HedgeConfigsList.css';


// Helper function to fix floating-point precision issues
const roundToFixed = (num, decimals = 10) => {
    return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
};

const initState = {
    id: 0,
    conf_descr: '',
    base_conn_id: 0,
    base_mrk: 'futures',
    base_symbol: '',
    base_dir: 'LONG',
    hedge_conn_id: 0,
    hedge_mrk: 'futures',
    hedge_symbol: '',
    exchange: 'binance',
    fees: 0.1, // 0.1 * 100
    mult: 1.0,
    open_perc: 0, // 1.0 * 100
    buff_perc: 2, // 0.5 * 100
    open_price: 0,
    open_dd: 0,
    open_qty: 0,
    close_all_sl_perc: 0, // 2.0 * 100
    trail_perc: 0, // 0.5 * 100
    safe_zone_mode: 'follow', // 'follow' or 'expand'
    hedge_tp_perc: 0, // 1.0 * 100
    hedge_tp_move_base: 0,
    incr_lvrg: 0,
    fix_lvrg: 0,
    blacklist: '',
    open_mode: 'market',
    close_mode: 'market',
    force_close_hedge: 0,
    force_close_all: 0,
    close_after_hours: 0,
    min_opened_time: 0,
    be_perc: 0.5,
    be_act_perc: 1,
    check_close_interval: 0,
    only_one_be: 0
}

const HedgeConfigsList = () => {
    const {t} = useTranslation();
    const [configs, setConfigs] = useState([]);
    const [filteredConfigs, setFilteredConfigs] = useState([]);
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingConfig, setEditingConfig] = useState(null);
    // const [expandedRows, setExpandedRows] = useState({});
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        name: '',
        baseSymbol: '',
        // hedgeSymbol: '',
        // baseConnection: '',
        hedgeConnection: '',
        status: 'all'
    });
    const navigate = useNavigate();

    // Форма для новой конфигурации
    const [formData, setFormData] = useState(initState);
    // State для отслеживания активных полей в каждой группе
    const [activeFields, setActiveFields] = useState({
        openGroup: 'open_perc', // open_perc, open_price или open_dd
        leverageGroup: 'incr_lvrg', // incr_lvrg или fix_lvrg
        sizeGroup: 'mult' // mult или open_qty
    });
    // Form mode: 'wizard' or 'pro'
    const [formMode, setFormMode] = useState('wizard');
    // Form validation errors
    const [formErrors, setFormErrors] = useState({});

    // Confirm dialog state
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => {},
        variant: 'warning',
        confirmText: t('common.confirm') || 'Confirm',
        cancelText: t('common.cancel') || 'Cancel'
    });

    // Dropdown menu state for each row
    const [openDropdown, setOpenDropdown] = useState(null);

    // Sorting state - default sort by Configuration Name (ID) descending
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openDropdown && !event.target.closest('.actions-dropdown')) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [openDropdown]);

    // Handle ESC key to close forms and dropdowns
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape') {
                // Close dropdown if open
                if (openDropdown) {
                    setOpenDropdown(null);
                    return;
                }
                // Close confirm dialog if open
                if (confirmDialog.isOpen) {
                    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                    return;
                }
                // Close form if open (same as Back button)
                if (showAddForm || editingConfig) {
                    setShowAddForm(false);
                    setEditingConfig(null);
                    setFormData(initState);
                    setFormErrors({});
                    return;
                }
                // Close filters if open
                if (showFilters) {
                    setShowFilters(false);
                }
            }
        };

        document.addEventListener('keydown', handleEscKey);
        return () => document.removeEventListener('keydown', handleEscKey);
    }, [openDropdown, confirmDialog.isOpen, showAddForm, editingConfig, showFilters]);

    // Toggle dropdown menu
    const toggleDropdown = (configId, e) => {
        e.stopPropagation();
        setOpenDropdown(openDropdown === configId ? null : configId);
    };

    // Handle sorting
    const handleSort = (field) => {
        if (sortField === field) {
            // Toggle direction if same field
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // New field, default to ascending
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Sort configs
    const sortConfigs = (configs) => {
        if (!sortField) return configs;

        return [...configs].sort((a, b) => {
            let aValue, bValue;

            switch (sortField) {
                case 'status':
                    aValue = a.active;
                    bValue = b.active;
                    break;
                case 'name':
                    // Sort by config ID
                    aValue = a.id;
                    bValue = b.id;
                    break;
                case 'asset':
                    aValue = `${a.base_symbol} ${a.hedge_symbol}`.toLowerCase();
                    bValue = `${b.base_symbol} ${b.hedge_symbol}`.toLowerCase();
                    break;
                case 'market':
                    aValue = a.base_mrk;
                    bValue = b.base_mrk;
                    break;
                case 'connection':
                    // Sort by connection ID
                    aValue = a.base_conn_id;
                    bValue = b.base_conn_id;
                    break;
                case 'connectionStatus':
                    aValue = isConnectionActive(a.base_conn_id) ? 1 : 0;
                    bValue = isConnectionActive(b.base_conn_id) ? 1 : 0;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    };

    // Загрузка списка конфигураций и подключений
    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            const headers = {Authorization: `Bearer ${token}`};

            // Загрузка подключений
            const connectionsResponse = await axios.get('/api/connections/', {headers});
            setConnections(connectionsResponse.data);

            // Загрузка конфигураций
            const configsResponse = await axios.get('/api/hedge-configs/', {headers});
            setConfigs(configsResponse.data);
            setError('');
        } catch (err) {
            setError(t('hedge.loadError'));
            if (err.response?.status === 401) {
                localStorage.removeItem('token');
                navigate('/login');
            }
        } finally {
            setLoading(false);
        }
    };

    // Функция для применения фильтров
    const applyFilters = () => {
        if (!configs.length) return;

        let result = [...configs];

        // Фільтрація за назвою
        if (filters.name) {
            result = result.filter(config =>
                config.conf_descr.toLowerCase().includes(filters.name.toLowerCase()) || config.id.toString().includes(filters.name.toLowerCase())
            );
        }

        // Фільтрація за базовим символом
        if (filters.baseSymbol) {
            result = result.filter(config =>
                config.base_symbol.toLowerCase().includes(filters.baseSymbol.toLowerCase())
            );
        }

        // Фільтрація за символом хеджування
        // if (filters.hedgeSymbol) {
        //     result = result.filter(config =>
        //         config.hedge_symbol.toLowerCase().includes(filters.hedgeSymbol.toLowerCase())
        //     );
        // }

        // Фільтрація за базовим підключенням
        // if (filters.baseConnection) {
        //     result = result.filter(config => {
        //         const connection = connections.find(conn => conn.id === config.base_conn_id);
        //         return connection && connection.conn_descr.toLowerCase().includes(filters.baseConnection.toLowerCase());
        //     });
        // }

        // Фільтрація за підключенням хеджування
        if (filters.hedgeConnection) {
            result = result.filter(config => {
                const connection = connections.find(conn => conn.id === config.hedge_conn_id);
                return connection && (connection.conn_descr.toLowerCase().includes(filters.hedgeConnection.toLowerCase()) || connection.id.toString().includes(filters.hedgeConnection.toLowerCase()));
            });
        }

        // Фільтрація за статусом
        if (filters.status !== 'all') {
            const activeStatus = filters.status === 'active' ? 1 : 0;
            result = result.filter(config => config.active === activeStatus);
        }

        setFilteredConfigs(result);
    };

    // Обробник зміни фільтрів
    const handleFilterChange = (e) => {
        const {name, value} = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Скидання фільтрів
    const resetFilters = () => {
        setFilters({
            name: '',
            baseSymbol: '',
            // hedgeSymbol: '',
            // baseConnection: '',
            hedgeConnection: '',
            status: 'all'
        });
    };

    // Загрузка при монтировании компонента
    useEffect(() => {
        fetchData().then(() => {
        });
    }, []);

    // Применение фильтров при изменении данных или фильтров
    useEffect(() => {
        applyFilters();
    }, [configs, filters, connections]);

    // Обработка изменения радио-кнопок для групп полей
    const handleRadioChange = (group, value) => {
        // Обновляем состояние активных полей
        setActiveFields(prev => ({
            ...prev,
            [group]: value
        }));

        // Сбрасываем неактивное поле на значение по умолчанию из initState
        const updatedFormData = {...formData};

        if (group === 'openGroup') {
            if (value === 'open_perc') {
                updatedFormData.open_price = initState.open_price; // Сброс open_price
                updatedFormData.open_dd = initState.open_dd; // Сброс open_dd
            } else if (value === 'open_price') {
                updatedFormData.open_perc = initState.open_perc; // Сброс open_perc
                updatedFormData.open_dd = initState.open_dd; // Сброс open_dd
            } else if (value === 'open_dd') {
                updatedFormData.open_price = 0; // Сброс open_price на 0
                updatedFormData.open_perc = 99; // Сброс open_perc на 0.99 * 100
            }
        } else if (group === 'leverageGroup') {
            if (value === 'incr_lvrg') {
                updatedFormData.fix_lvrg = initState.fix_lvrg; // Сброс fix_lvrg
            } else {
                updatedFormData.incr_lvrg = initState.incr_lvrg; // Сброс incr_lvrg
            }
        } else if (group === 'sizeGroup') {
            if (value === 'mult') {
                updatedFormData.open_qty = initState.open_qty; // Сброс open_qty
            } else {
                updatedFormData.mult = initState.mult; // Сброс mult на значение по умолчанию
            }
        }

        setFormData(updatedFormData);
    };

    // Validation function for inline validation
    const validateField = (fieldName, value) => {
        const errors = {};

        switch (fieldName) {
            case 'conf_descr':
                if (!value || !value.toString().trim()) {
                    errors[fieldName] = t('hedge.validation.nameRequired') || 'Configuration name is required';
                }
                break;

            case 'base_symbol':
            case 'hedge_symbol':
                if (!value || !value.toString().trim()) {
                    errors[fieldName] = t('hedge.validation.symbolRequired') || 'Symbol is required';
                } else if (!/^[A-Z0-9]+$/i.test(value.toString())) {
                    errors[fieldName] = t('hedge.validation.symbolFormat') || 'Symbol must contain only letters and numbers';
                }
                break;

            case 'base_conn_id':
            case 'hedge_conn_id':
                if (!value || value === 0) {
                    errors[fieldName] = t('hedge.validation.connectionRequired') || 'Connection is required';
                }
                break;

            case 'be_act_perc':
                const buffPerc = parseFloat(formData.buff_perc);
                const beActPerc = parseFloat(value) || 0;
                if (beActPerc > buffPerc) {
                    errors[fieldName] = t('hedge.validation.beActPercExceedsBuffPerc') || 'Activation % must be less than or equal to buffer %';
                }
                break;

            case 'fees':
                const fees = parseFloat(value) || 0;
                if (fees < 0 || fees > 10) {
                    errors[fieldName] = t('hedge.validation.feesRange') || 'Fees must be between 0 and 10%';
                }
                break;

            case 'mult':
                const mult = parseFloat(value) || 0;
                if (mult < 0 || mult > 100) {
                    errors[fieldName] = t('hedge.validation.multRange') || 'Multiplier must be between 0 and 100';
                }
                break;

            case 'buff_perc':
                const buff = parseFloat(value);
                if (buff <= 0) {
                    errors[fieldName] = t('hedge.validation.buffPercMin') || 'Buffer % must be positive';
                }
                break;

            case 'check_close_interval':
                const interval = parseInt(value) || 0;
                if (interval < 0) {
                    errors[fieldName] = t('hedge.validation.intervalMin') || 'Interval must be non-negative';
                }
                break;
        }

        return errors;
    };

    // Обработка изменения полей формы
    const handleInputChange = (e) => {
        const {name, value} = e.target;
        let processedValue = value;

        // Конвертация числовых значений
        if ([
            'fees', 'mult', 'open_perc', 'buff_perc', 'open_price', 'open_dd', 'open_qty',
            'close_all_sl_perc', 'trail_perc', 'hedge_tp_perc', 'be_perc', 'be_act_perc'
        ].includes(name)) {
            processedValue = roundToFixed(parseFloat(value) || 0);
        } else if ([
            'hedge_tp_move_base', 'incr_lvrg', 'fix_lvrg', 'force_close_hedge',
            'force_close_all', 'close_after_hours', 'min_opened_time', 'check_close_interval', 'only_one_be'
        ].includes(name)) {
            processedValue = parseInt(value) || 0;
        }

        // Создаем новый объект для обновления состояния
        const updatedFormData = {
            ...formData,
            [name]: processedValue
        };

        // Если изменяется base_mrk на fixprice, устанавливаем значения по умолчанию
        if (name === 'base_mrk' && value === 'fixprice') {
            updatedFormData.base_conn_id = updatedFormData.hedge_conn_id;
            updatedFormData.base_dir = 'LONG';
            updatedFormData.mult = 1;
            updatedFormData.open_perc = 0;
            updatedFormData.hedge_tp_perc = 0;
            updatedFormData.hedge_tp_move_base = 0;
            updatedFormData.incr_lvrg = 0;
            updatedFormData.fix_lvrg = 0;
            updatedFormData.close_after_hours = 0;
            updatedFormData.force_close_all = 0;
        }

        if (name === 'base_mrk' && value === 'spot') {
            updatedFormData.incr_lvrg = 0;
        }

        setFormData(updatedFormData);

        // Автоматическая установка биржи при выборе подключения для хеджирования
        if (name === 'hedge_conn_id') {
            const selectedConnection = connections.find(conn => conn.id === parseInt(value));
            if (selectedConnection) {
                setFormData(prev => ({
                    ...prev,
                    exchange: selectedConnection.exchange
                }));
            }
        }

        // Inline validation
        const fieldErrors = validateField(name, processedValue);
        setFormErrors(prev => {
            const updated = {...prev};
            if (Object.keys(fieldErrors).length > 0) {
                Object.assign(updated, fieldErrors);
            } else {
                delete updated[name];
            }
            return updated;
        });
    };

    // Додавання нової конфігурації
    const handleAddConfig = async (e) => {
        e.preventDefault();

        // Активація без збитку не повинна перевищувати буферну зону
        if (formData.be_act_perc > formData.buff_perc) {
            setError(t('hedge.validation.beActPercExceedsBuffPerc'));
            return;
        }

        if (formData.be_perc > formData.be_act_perc) {
            setError(t('hedge.validation.bePercExceedsBeActPerc'));
            return;
        }

        if (formData.check_close_interval < 0) {
            setError(t('hedge.validation.checkCloseIntervalNegative'));
            return;
        }

        if (![0,1].includes(parseInt(formData.only_one_be))) {
            setError(t('hedge.validation.onlyOneBeInvalid'));
            return;
        }

        try {
            const token = localStorage.getItem('token');

            // Створюємо копію даних форми для відправки
            const dataToSend = {...formData};

            // Ділимо відсоткові значення на 100 перед збереженням
            dataToSend.fees = roundToFixed(formData.fees / 100);
            dataToSend.open_perc = roundToFixed(formData.open_perc / 100);
            dataToSend.buff_perc = roundToFixed(formData.buff_perc / 100);
            dataToSend.close_all_sl_perc = roundToFixed(formData.close_all_sl_perc / 100);
            dataToSend.trail_perc = roundToFixed(formData.trail_perc / 100);
            dataToSend.hedge_tp_perc = roundToFixed(formData.hedge_tp_perc / 100);
            dataToSend.be_perc = roundToFixed(formData.be_perc / 100);
            dataToSend.be_act_perc = roundToFixed(formData.be_act_perc / 100);

            // Якщо вибрано open_dd, встановлюємо правильні значення
            if (activeFields.openGroup === 'open_dd') {
                dataToSend.open_perc = 0.99;
                dataToSend.open_price = 0;
            }

            await axios.post('/api/hedge-configs/', dataToSend, {
                headers: {Authorization: `Bearer ${token}`}
            });

            // Очищення форми і оновлення списку
            resetForm();
            setShowAddForm(false);
            await fetchData();
            setError('');
        } catch (err) {
            setError(err.response?.data?.detail || t('hedge.addError'));
        }
    };

    // Редактирование конфигурации
    const handleEditConfig = async (e) => {
        e.preventDefault();

        if (formData.be_act_perc > formData.buff_perc) {
            setError(t('hedge.validation.beActPercExceedsBuffPerc'));
            return;
        }

        if (formData.be_perc > formData.be_act_perc) {
            setError(t('hedge.validation.bePercExceedsBeActPerc'));
            return;
        }

        if (formData.check_close_interval < 0) {
            setError(t('hedge.validation.checkCloseIntervalNegative'));
            return;
        }

        if (![0,1].includes(parseInt(formData.only_one_be))) {
            setError(t('hedge.validation.onlyOneBeInvalid'));
            return;
        }

        try {
            const token = localStorage.getItem('token');

            // Створюємо копію даних форми для відправки
            const dataToSend = {...formData};

            // Ділимо відсоткові значення на 100 перед збереженням
            dataToSend.fees = roundToFixed(formData.fees / 100);
            dataToSend.open_perc = roundToFixed(formData.open_perc / 100);
            dataToSend.buff_perc = roundToFixed(formData.buff_perc / 100);
            dataToSend.close_all_sl_perc = roundToFixed(formData.close_all_sl_perc / 100);
            dataToSend.trail_perc = roundToFixed(formData.trail_perc / 100);
            dataToSend.hedge_tp_perc = roundToFixed(formData.hedge_tp_perc / 100);
            dataToSend.be_perc = roundToFixed(formData.be_perc / 100);
            dataToSend.be_act_perc = roundToFixed(formData.be_act_perc / 100);

            // Якщо вибрано open_dd, встановлюємо правильні значення
            if (activeFields.openGroup === 'open_dd') {
                dataToSend.open_perc = 0.99;
                dataToSend.open_price = 0;
            }

            await axios.put(`/api/hedge-configs/${editingConfig.id}`, dataToSend, {
                headers: {Authorization: `Bearer ${token}`}
            });

            setEditingConfig(null);
            await fetchData();
            setError('');
        } catch (err) {
            setError(err.response?.data?.detail || t('hedge.updateError'));
        }
    };

    // Начало редактирования конфигурации
    const startEditing = (config) => {
        setShowFilters(false);
        setEditingConfig(config);

        // Определяем активные поля на основе значений конфигурации
        const newActiveFields = {
            // Если open_dd имеет значение, то активно оно, иначе проверяем open_price
            openGroup: (config.open_dd && config.open_dd > 0) ? 'open_dd' : (config.open_price > 0 ? 'open_price' : 'open_perc'),
            // Если fix_lvrg имеет значение, то активно оно, иначе incr_lvrg
            leverageGroup: config.fix_lvrg > 0 ? 'fix_lvrg' : 'incr_lvrg',
            // Если open_qty имеет значение, то активно оно, иначе mult
            sizeGroup: config.open_qty > 0 ? 'open_qty' : 'mult'
        };

        setActiveFields(newActiveFields);

        setFormData({
            conf_descr: config.conf_descr,
            base_conn_id: config.base_conn_id,
            base_mrk: config.base_mrk,
            base_symbol: config.base_symbol,
            base_dir: config.base_dir ? config.base_dir.toUpperCase() : 'LONG',
            hedge_conn_id: config.hedge_conn_id,
            hedge_mrk: config.hedge_mrk,
            hedge_symbol: config.hedge_symbol,
            exchange: config.exchange,
            fees: roundToFixed(config.fees * 100),
            mult: roundToFixed(config.mult),
            open_perc: roundToFixed(config.open_perc * 100),
            buff_perc: roundToFixed(config.buff_perc * 100),
            open_price: roundToFixed(config.open_price),
            open_dd: roundToFixed(config.open_dd || 0),
            open_qty: roundToFixed(config.open_qty),
            close_all_sl_perc: roundToFixed(config.close_all_sl_perc * 100),
            trail_perc: roundToFixed(config.trail_perc * 100),
            hedge_tp_perc: roundToFixed(config.hedge_tp_perc * 100),
            hedge_tp_move_base: config.hedge_tp_move_base,
            incr_lvrg: config.incr_lvrg,
            fix_lvrg: config.fix_lvrg,
            blacklist: config.blacklist || '',
            open_mode: config.open_mode,
            close_mode: config.open_mode, // close_mode samе as open_mode
            force_close_hedge: config.force_close_hedge,
            force_close_all: config.force_close_all,
            close_after_hours: config.close_after_hours,
            min_opened_time: config.min_opened_time,
            be_perc: roundToFixed((config.be_perc || 0.5) * 100),
            be_act_perc: roundToFixed((config.be_act_perc || 1.0) * 100),
            safe_zone_mode: config.safe_zone_mode || 'follow',
            check_close_interval: config.check_close_interval || 0,
            only_one_be: config.only_one_be || 0
        });
        setShowAddForm(false);
    };

    // Изменение активности конфигурации
    const handleToggleActive = (id, currentActive) => {
        const newActive = currentActive === 1 ? 0 : 1;
        const actionText = newActive === 1
            ? t('hedge.confirmActivate') || 'Are you sure you want to ACTIVATE this configuration? It will start managing positions.'
            : t('hedge.confirmDeactivate') || 'Are you sure you want to DEACTIVATE this configuration? It will stop managing positions.';

        setConfirmDialog({
            isOpen: true,
            title: newActive === 1 ? t('hedge.activate') : t('hedge.deactivate'),
            message: actionText,
            variant: 'info',
            confirmText: t('common.confirm') || 'Confirm',
            cancelText: t('common.cancel') || 'Cancel',
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                await performToggleActive(id, newActive);
            }
        });
    };

    const performToggleActive = async (id, newActive) => {
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`/api/hedge-configs/${id}/activate/${newActive}`, {}, {
                headers: {Authorization: `Bearer ${token}`}
            });

            await fetchData();
            setError('');
        } catch (err) {
            setError(err.response?.data?.detail || t('hedge.updateError'));
        }
    };

    // Force Close Hedge - закрыть только хедж позицию
    const handleForceCloseHedge = (config) => {
        // Get localized warning text and replace placeholders
        let warningText = t('hedge.forceCloseHedgeWarning');

        if (warningText && warningText.includes('{')) {
            // Replace placeholders with actual values
            warningText = warningText
                .replace('{conf_descr}', config.conf_descr)
                .replace('{hedge_symbol}', config.hedge_symbol);
        } else {
            // Fallback if translation not found
            warningText = `⚠️ WARNING: FORCE CLOSE HEDGE\n\n` +
                `This will IMMEDIATELY close the HEDGE position for:\n` +
                `Config: ${config.conf_descr}\n` +
                `Symbol: ${config.hedge_symbol}\n\n` +
                `This action:\n` +
                `• Closes hedge position at market price\n` +
                `• Cannot be undone\n` +
                `• May result in losses if market moves against you\n\n` +
                `Do you understand and want to proceed?`;
        }

        setConfirmDialog({
            isOpen: true,
            title: '⚠️ ' + (t('hedge.forceCloseHedge') || 'Force Close Hedge'),
            message: warningText,
            variant: 'warning',
            confirmText: t('common.confirm') || 'Yes, Force Close',
            cancelText: t('common.cancel') || 'Cancel',
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                await performForceCloseHedge(config);
            }
        });
    };

    const performForceCloseHedge = async (config) => {
        try{
            const token = localStorage.getItem('token');

            // Prepare full config data with force_close_hedge set to 1
            const dataToSend = {
                conf_descr: config.conf_descr,
                exchange: config.exchange,  // Required field
                base_mrk: config.base_mrk,
                base_conn_id: config.base_conn_id,
                base_symbol: config.base_symbol,
                base_dir: config.base_dir,
                hedge_mrk: config.hedge_mrk,
                hedge_conn_id: config.hedge_conn_id,
                hedge_symbol: config.hedge_symbol,
                mult: config.mult,
                open_qty: config.open_qty,
                incr_lvrg: config.incr_lvrg,
                fix_lvrg: config.fix_lvrg,
                open_price: config.open_price,
                open_dd: config.open_dd,
                close_after_hours: config.close_after_hours,
                min_opened_time: config.min_opened_time,
                be_perc: config.be_perc,
                be_act_perc: config.be_act_perc,
                check_close_interval: config.check_close_interval,
                only_one_be: config.only_one_be,
                safe_zone_mode: config.safe_zone_mode,
                hedge_tp_move_base: config.hedge_tp_move_base,
                open_mode: config.open_mode,
                close_mode: config.close_mode,  // Required field
                // Percentage fields as decimals (already in correct format from DB)
                fees: config.fees,
                open_perc: config.open_perc,
                buff_perc: config.buff_perc,
                close_all_sl_perc: config.close_all_sl_perc,
                trail_perc: config.trail_perc,
                hedge_tp_perc: config.hedge_tp_perc,
                // Force close fields
                force_close_hedge: 1,  // SET TO 1
                force_close_all: 0
            };

            await axios.put(`/api/hedge-configs/${config.id}`, dataToSend, {
                headers: {Authorization: `Bearer ${token}`}
            });

            await fetchData();
            setError('');
        } catch (err) {
            console.error('Force close hedge error:', err.response?.data);
            // Handle validation errors (422)
            if (err.response?.data?.detail) {
                const detail = err.response.data.detail;
                if (Array.isArray(detail)) {
                    // Validation error format
                    const errorMessages = detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join('\n');
                    setError(errorMessages);
                } else if (typeof detail === 'string') {
                    setError(detail);
                } else {
                    setError(JSON.stringify(detail));
                }
            } else {
                setError(t('hedge.updateError'));
            }
        }
    };

    // Force Close All - закрыть все позиции
    const handleForceCloseAll = (config) => {
        // Get localized warning text and replace placeholders
        let warningText = t('hedge.forceCloseAllWarning');

        if (warningText && warningText.includes('{')) {
            // Replace placeholders with actual values
            warningText = warningText
                .replace('{conf_descr}', config.conf_descr)
                .replace('{base_symbol}', config.base_symbol)
                .replace('{hedge_symbol}', config.hedge_symbol);
        } else {
            // Fallback if translation not found
            warningText = `⚠️⚠️⚠️ CRITICAL WARNING: FORCE CLOSE ALL POSITIONS ⚠️⚠️⚠️\n\n` +
                `This will IMMEDIATELY close ALL POSITIONS for:\n` +
                `Config: ${config.conf_descr}\n` +
                `Base: ${config.base_symbol}\n` +
                `Hedge: ${config.hedge_symbol}\n\n` +
                `This action:\n` +
                `• Closes BOTH base AND hedge positions at market price\n` +
                `• Cannot be undone\n` +
                `• Will exit your entire position immediately\n` +
                `• May result in significant losses\n\n` +
                `This is a CRITICAL operation!\n` +
                `Do you FULLY understand and want to proceed?`;
        }

        // First confirmation
        setConfirmDialog({
            isOpen: true,
            title: '⛔ ' + (t('hedge.forceCloseAll') || 'Force Close ALL Positions'),
            message: warningText,
            variant: 'critical',
            confirmText: t('common.continue') || 'Continue',
            cancelText: t('common.cancel') || 'Cancel',
            onConfirm: () => {
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                // Show second confirmation
                showForceCloseAllDoubleConfirm(config);
            }
        });
    };

    const showForceCloseAllDoubleConfirm = (config) => {
        const doubleConfirmText = t('hedge.forceCloseAllDoubleConfirm') ||
            `FINAL CONFIRMATION:\n\nYou are about to close ALL positions.\nThis cannot be undone.\n\nClick OK to proceed or Cancel to abort.`;

        setConfirmDialog({
            isOpen: true,
            title: '⛔ ' + (t('hedge.forceCloseAll') || 'FINAL CONFIRMATION'),
            message: doubleConfirmText,
            variant: 'critical',
            confirmText: t('common.confirm') || 'Yes, Close ALL',
            cancelText: t('common.cancel') || 'Cancel',
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                await performForceCloseAll(config);
            }
        });
    };

    const performForceCloseAll = async (config) => {
        try {
            const token = localStorage.getItem('token');

            // Prepare full config data with force_close_all set to 1
            const dataToSend = {
                conf_descr: config.conf_descr,
                exchange: config.exchange,  // Required field
                base_mrk: config.base_mrk,
                base_conn_id: config.base_conn_id,
                base_symbol: config.base_symbol,
                base_dir: config.base_dir,
                hedge_mrk: config.hedge_mrk,
                hedge_conn_id: config.hedge_conn_id,
                hedge_symbol: config.hedge_symbol,
                mult: config.mult,
                open_qty: config.open_qty,
                incr_lvrg: config.incr_lvrg,
                fix_lvrg: config.fix_lvrg,
                open_price: config.open_price,
                open_dd: config.open_dd,
                close_after_hours: config.close_after_hours,
                min_opened_time: config.min_opened_time,
                be_perc: config.be_perc,
                be_act_perc: config.be_act_perc,
                check_close_interval: config.check_close_interval,
                only_one_be: config.only_one_be,
                safe_zone_mode: config.safe_zone_mode,
                hedge_tp_move_base: config.hedge_tp_move_base,
                open_mode: config.open_mode,
                close_mode: config.close_mode,  // Required field
                // Percentage fields as decimals (already in correct format from DB)
                fees: config.fees,
                open_perc: config.open_perc,
                buff_perc: config.buff_perc,
                close_all_sl_perc: config.close_all_sl_perc,
                trail_perc: config.trail_perc,
                hedge_tp_perc: config.hedge_tp_perc,
                // Force close fields
                force_close_hedge: 0,
                force_close_all: 1  // SET TO 1
            };

            await axios.put(`/api/hedge-configs/${config.id}`, dataToSend, {
                headers: {Authorization: `Bearer ${token}`}
            });

            await fetchData();
            setError('');
        } catch (err) {
            console.error('Force close all error:', err.response?.data);
            // Handle validation errors (422)
            if (err.response?.data?.detail) {
                const detail = err.response.data.detail;
                if (Array.isArray(detail)) {
                    // Validation error format
                    const errorMessages = detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join('\n');
                    setError(errorMessages);
                } else if (typeof detail === 'string') {
                    setError(detail);
                } else {
                    setError(JSON.stringify(detail));
                }
            } else {
                setError(t('hedge.updateError'));
            }
        }
    };

    // Удаление конфигурации
    const handleDeleteConfig = (id) => {
        setConfirmDialog({
            isOpen: true,
            title: t('common.delete') || 'Delete Configuration',
            message: t('hedge.deleteConfirm') || 'Are you sure you want to delete this configuration?',
            variant: 'warning',
            confirmText: t('common.delete') || 'Delete',
            cancelText: t('common.cancel') || 'Cancel',
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                await performDeleteConfig(id);
            }
        });
    };

    const performDeleteConfig = async (id) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/hedge-configs/${id}`, {
                headers: {Authorization: `Bearer ${token}`}
            });

            await fetchData();
            setError('');
        } catch (err) {
            setError(err.response?.data?.detail || t('hedge.deleteError'));
        }
    };

    // Дублирование конфигурации
    const handleDuplicateConfig = (config) => {
        setShowFilters(false);

        // Создаем копию конфигурации без id
        const duplicatedConfig = {...config};
        delete duplicatedConfig.id;

        // Устанавливаем данные формы из копии конфигурации
        setFormData({
            conf_descr: duplicatedConfig.conf_descr,
            base_conn_id: duplicatedConfig.base_conn_id,
            base_mrk: duplicatedConfig.base_mrk,
            base_symbol: duplicatedConfig.base_symbol,
            base_dir: duplicatedConfig.base_dir ? duplicatedConfig.base_dir.toUpperCase() : 'LONG',
            hedge_conn_id: duplicatedConfig.hedge_conn_id,
            hedge_mrk: duplicatedConfig.hedge_mrk,
            hedge_symbol: duplicatedConfig.hedge_symbol,
            exchange: duplicatedConfig.exchange,
            fees: roundToFixed(duplicatedConfig.fees * 100),
            mult: roundToFixed(duplicatedConfig.mult),
            open_perc: roundToFixed(duplicatedConfig.open_perc * 100),
            buff_perc: roundToFixed(duplicatedConfig.buff_perc * 100),
            open_price: roundToFixed(duplicatedConfig.open_price),
            open_dd: roundToFixed(duplicatedConfig.open_dd || 0),
            open_qty: roundToFixed(duplicatedConfig.open_qty),
            close_all_sl_perc: roundToFixed(duplicatedConfig.close_all_sl_perc * 100),
            trail_perc: roundToFixed(duplicatedConfig.trail_perc * 100),
            hedge_tp_perc: roundToFixed(duplicatedConfig.hedge_tp_perc * 100),
            hedge_tp_move_base: duplicatedConfig.hedge_tp_move_base,
            incr_lvrg: duplicatedConfig.incr_lvrg,
            fix_lvrg: duplicatedConfig.fix_lvrg,
            blacklist: duplicatedConfig.blacklist || '',
            open_mode: duplicatedConfig.open_mode,
            close_mode: duplicatedConfig.open_mode, // close_mode same as open_mode
            force_close_hedge: duplicatedConfig.force_close_hedge,
            force_close_all: duplicatedConfig.force_close_all,
            close_after_hours: duplicatedConfig.close_after_hours,
            min_opened_time: duplicatedConfig.min_opened_time,
            be_perc: roundToFixed((duplicatedConfig.be_perc || 0.5) * 100),
            be_act_perc: roundToFixed((duplicatedConfig.be_act_perc || 1.0) * 100),
            safe_zone_mode: duplicatedConfig.safe_zone_mode || 'follow',
            check_close_interval: duplicatedConfig.check_close_interval || 0,
            only_one_be: duplicatedConfig.only_one_be || 0
        });

        // Открываем форму для добавления новой конфигурации
        setShowAddForm(true);
        setEditingConfig(null);
    };

    // Сброс формы до начальных значений
    const resetForm = () => {
        setFormData(initState);
        // Сброс активных полей до значений по умолчанию
        setActiveFields({
            openGroup: 'open_perc',
            leverageGroup: 'incr_lvrg',
            sizeGroup: 'mult'
        });
    };

    // Получение названия подключения по ID
    const getConnectionName = (connId) => {
        const connection = connections.find(conn => conn.id === connId);
        return connection ? connection.conn_descr : t('hedge.unknownConnection');
    };

    // Получение названия биржи по ID подключения
    const getExchangeName = (connId) => {
        const connection = connections.find(conn => conn.id === connId);
        return connection ? connection.exchange : t('hedge.unknownExchange');
    };

    // Проверка активности подключения по ID
    const isConnectionActive = (connId) => {
        const connection = connections.find(conn => conn.id === connId);
        return connection ? connection.active === 1 : false;
    };

    const getMarketName = (connType) => {
        if (connType === 'futures') {
            return t('hedge.marketFutures');
        }
        if (connType === 'spot') {
            return t('hedge.marketSpot');
        }
        if (connType === 'fixprice') {
            return t('hedge.marketFixprice');
        }
    };

    // Функция для разворачивания/сворачивания строки
    // const toggleRowExpand = (configId) => {
    //     setExpandedRows(prev => ({
    //         ...prev,
    //         [configId]: !prev[configId]
    //     }));
    // };

    return (
        <div className="hedge-configs-container">
            <div className="header-with-actions">
                <h2>{t('hedge.title')}</h2>
                <div className="configs-actions">
                    {!(showAddForm || editingConfig) && (
                        <>
                            <button
                                className="add-button"
                                onClick={() => {
                                    if (!editingConfig) setShowAddForm(!showAddForm);
                                    setEditingConfig(null);
                                    setShowFilters(false);
                                    resetForm();
                                }}
                            >
                                ✚ {t('hedge.addConfig')}
                            </button>
                            <button
                                className="filter-button"
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                {showFilters ? t('common.hideFilters') : t('common.showFilters')}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {/* Фильтры для поиска конфигураций */}
            {showFilters && (
                <div className="filters-container">
                    <h3>{t('hedge.filters')}</h3>
                    <div className="filters-form">
                        <div className="filter-group">
                            <label htmlFor="name">{t('hedge.nameFilter')}</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={filters.name}
                                onChange={handleFilterChange}
                                placeholder={t('hedge.nameFilterPlaceholder')}
                            />
                        </div>

                        <div className="filter-group">
                            <label htmlFor="baseSymbol">{t('hedge.baseSymbolFilter')}</label>
                            <input
                                type="text"
                                id="baseSymbol"
                                name="baseSymbol"
                                value={filters.baseSymbol}
                                onChange={handleFilterChange}
                                placeholder={t('hedge.baseSymbolFilterPlaceholder')}
                            />
                        </div>

                        <div className="filter-group">
                            <label htmlFor="hedgeConnection">{t('hedge.hedgeConnectionFilter')}</label>
                            <input
                                type="text"
                                id="hedgeConnection"
                                name="hedgeConnection"
                                value={filters.hedgeConnection}
                                onChange={handleFilterChange}
                                placeholder={t('hedge.hedgeConnectionFilterPlaceholder')}
                            />
                        </div>

                        <div className="filter-group">
                            <label htmlFor="status">{t('common.status')}</label>
                            <CustomSelect
                                id="status"
                                name="status"
                                value={filters.status}
                                onChange={handleFilterChange}
                                options={[
                                    { value: 'all', label: t('common.all') },
                                    { value: 'active', label: t('common.active') },
                                    { value: 'inactive', label: t('common.inactive') }
                                ]}
                            />
                        </div>
                    </div>

                    <div className="filters-actions">
                        <button
                            className="reset-filters-button"
                            onClick={resetFilters}
                        >
                            {t('common.resetFilters')}
                        </button>
                    </div>
                </div>
            )}

            {/* Форма добавления/редактирования конфигурации */}
            {(showAddForm || editingConfig) && (
                <div className="config-form-container">
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                        <h3>{editingConfig ? t('hedge.editConfig') : t('hedge.newConfig')}</h3>

                        <div style={{display: 'flex', gap: '1rem', alignItems: 'center'}}>
                            {/* Mode Toggle */}
                            <div className="mode-toggle">
                                <button
                                    type="button"
                                    className={`mode-btn ${formMode === 'wizard' ? 'active' : ''}`}
                                    onClick={() => setFormMode('wizard')}
                                >
                                    <Zap size={18} />
                                    {t('hedge.wizardMode') || 'Wizard'}
                                </button>
                                <button
                                    type="button"
                                    className={`mode-btn ${formMode === 'pro' ? 'active' : ''}`}
                                    onClick={() => setFormMode('pro')}
                                >
                                    <Settings size={18} />
                                    {t('hedge.proMode') || 'Pro'}
                                </button>
                            </div>

                            <button
                                type="button"
                                className="cancel-button"
                                onClick={() => {
                                    setShowAddForm(false);
                                    setEditingConfig(null);
                                    setFormData(initState);
                                    setFormErrors({});
                                }}
                            >
                                {t('common.back')}
                            </button>
                        </div>
                    </div>

                    {/* Conditional Render: Wizard Mode or Pro Mode */}
                    {formMode === 'wizard' ? (
                        <HedgeWizard
                            formData={formData}
                            setFormData={setFormData}
                            activeFields={activeFields}
                            setActiveFields={setActiveFields}
                            connections={connections}
                            onSubmit={editingConfig ? handleEditConfig : handleAddConfig}
                            onCancel={() => {
                                setShowAddForm(false);
                                setEditingConfig(null);
                                setFormData(initState);
                                setFormErrors({});
                            }}
                            editingConfig={editingConfig}
                            handleInputChange={handleInputChange}
                            handleRadioChange={handleRadioChange}
                            errors={formErrors}
                            t={t}
                        />
                    ) : (
                        <form onSubmit={editingConfig ? handleEditConfig : handleAddConfig}>

                        <div className="form-group">
                            <label htmlFor="conf_descr">
                                {t('hedge.configName')}
                                <InfoTooltip text={t('hedge.tooltips.configName')} />
                            </label>
                            <input
                                type="text"
                                id="conf_descr"
                                name="conf_descr"
                                value={formData.conf_descr}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="form-grid">
                            <div className="form-section">
                                <h4>{t('hedge.mainParameters')}</h4>

                                <div className="form-group">
                                    <label htmlFor="base_mrk">
                                        {t('hedge.baseMarket')}
                                        <InfoTooltip text={t('hedge.tooltips.baseMarket')} />
                                    </label>
                                    <CustomSelect
                                        id="base_mrk"
                                        name="base_mrk"
                                        value={formData.base_mrk}
                                        onChange={handleInputChange}
                                        required
                                        options={[
                                            { value: 'futures', label: t('hedge.marketFutures') },
                                            { value: 'spot', label: t('hedge.marketSpot') },
                                            { value: 'fixprice', label: t('hedge.marketFixprice') }
                                        ]}
                                    />
                                </div>

                                {formData.base_mrk !== 'fixprice' && (
                                    <div className="form-group">
                                        <label htmlFor="base_conn_id">
                                            {t('hedge.baseConnectionTitle')}
                                            <InfoTooltip text={t('hedge.tooltips.baseConnection')} />
                                        </label>
                                        <CustomSelect
                                            id="base_conn_id"
                                            name="base_conn_id"
                                            value={formData.base_conn_id}
                                            onChange={handleInputChange}
                                            required
                                            options={[
                                                { value: '', label: t('hedge.selectConnection') },
                                                ...connections.filter(conn => conn.active === 1).map(conn => ({
                                                    value: conn.id,
                                                    label: `${conn.id} : ${conn.conn_descr} (${conn.exchange})`
                                                }))
                                            ]}
                                        />
                                    </div>
                                )}

                                <div className="form-group">
                                    <label htmlFor="base_symbol">
                                        {t('hedge.baseCoinName')}
                                        <InfoTooltip text={t('hedge.tooltips.baseSymbol')} />
                                    </label>
                                    <input
                                        type="text"
                                        id="base_symbol"
                                        name="base_symbol"
                                        value={formData.base_symbol}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                {formData.base_mrk !== 'fixprice' && (
                                    <div className="form-group">
                                        <label htmlFor="base_dir">
                                            {t('hedge.baseDirection')}
                                            <InfoTooltip text={t('hedge.tooltips.baseDirection')} />
                                        </label>
                                        <CustomSelect
                                            id="base_dir"
                                            name="base_dir"
                                            value={formData.base_dir}
                                            onChange={handleInputChange}
                                            required
                                            options={[
                                                { value: 'LONG', label: t('hedge.directionLong') },
                                                { value: 'SHORT', label: t('hedge.directionShort') }
                                            ]}
                                        />
                                    </div>
                                )}

                                <div className="form-group">
                                    <label htmlFor="hedge_mrk">
                                        {t('hedge.hedgeMarket')}
                                        <InfoTooltip text={t('hedge.tooltips.hedgeMarket')} />
                                    </label>
                                    <CustomSelect
                                        id="hedge_mrk"
                                        name="hedge_mrk"
                                        value={formData.hedge_mrk}
                                        onChange={handleInputChange}
                                        required
                                        options={[
                                            { value: 'futures', label: t('hedge.marketFutures') },
                                            { value: 'spot', label: t('hedge.marketSpot') }
                                        ]}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="hedge_conn_id">
                                        {t('hedge.hedgeConnection')}
                                        <InfoTooltip text={t('hedge.tooltips.hedgeConnection')} />
                                    </label>
                                    <CustomSelect
                                        id="hedge_conn_id"
                                        name="hedge_conn_id"
                                        value={formData.hedge_conn_id}
                                        onChange={handleInputChange}
                                        required
                                        options={[
                                            { value: '', label: t('hedge.selectConnection') },
                                            ...connections.filter(conn => conn.active === 1).map(conn => ({
                                                value: conn.id,
                                                label: `${conn.id} : ${conn.conn_descr} (${conn.exchange})`
                                            }))
                                        ]}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="hedge_symbol">
                                        {t('hedge.hedgeCoinName')}
                                        <InfoTooltip text={t('hedge.tooltips.hedgeSymbol')} />
                                    </label>
                                    <input
                                        type="text"
                                        id="hedge_symbol"
                                        name="hedge_symbol"
                                        value={formData.hedge_symbol}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="fees">
                                        {t('hedge.fees')}
                                        <InfoTooltip text={t('hedge.tooltips.fees')} />
                                    </label>
                                    <input
                                        type="number"
                                        id="fees"
                                        name="fees"
                                        value={formData.fees}
                                        onChange={handleInputChange}
                                        step="any"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-section">
                                <h4>{t('hedge.hedgeParameters')}</h4>

                                {formData.base_mrk === 'fixprice' ? (
                                    <div className="form-group">
                                        <label htmlFor="mult">
                                            {t('hedge.openQuantity')}
                                            <InfoTooltip text={t('hedge.tooltips.openQuantity')} />
                                        </label>
                                        <input
                                            type="number"
                                            id="open_qty"
                                            name="open_qty"
                                            value={formData.open_qty}
                                            onChange={handleInputChange}
                                            step="any"
                                            required
                                        />
                                    </div>
                                ) : (
                                    <div className="form-group-radio">
                                        <div className="radio-group">
                                            <div className="radio-option">
                                                <input
                                                    type="radio"
                                                    id="mult"
                                                    name="sizeGroup"
                                                    checked={activeFields.sizeGroup === 'mult'}
                                                    onChange={() => handleRadioChange('sizeGroup', 'mult')}
                                                />
                                                <label htmlFor="mult">
                                                    {t('hedge.multiplier')}
                                                    <InfoTooltip text={t('hedge.tooltips.multiplier')} />
                                                </label>
                                            </div>
                                            <input
                                                type="number"
                                                id="mult"
                                                name="mult"
                                                value={formData.mult}
                                                onChange={handleInputChange}
                                                step="0.01"
                                                min="0"
                                                max="10"
                                                disabled={activeFields.sizeGroup !== 'mult'}
                                                required={activeFields.sizeGroup === 'mult'}
                                            />
                                        </div>

                                        <div className="radio-group">
                                            <div className="radio-option">
                                                <input
                                                    type="radio"
                                                    id="open_qty"
                                                    name="sizeGroup"
                                                    checked={activeFields.sizeGroup === 'open_qty'}
                                                    onChange={() => handleRadioChange('sizeGroup', 'open_qty')}
                                                />
                                                <label htmlFor="open_qty">
                                                    {t('hedge.openQuantity')}
                                                    <InfoTooltip text={t('hedge.tooltips.openQuantity')} />
                                                </label>
                                            </div>
                                            <input
                                                type="number"
                                                id="open_qty"
                                                name="open_qty"
                                                value={formData.open_qty}
                                                onChange={handleInputChange}
                                                step="any"
                                                disabled={activeFields.sizeGroup !== 'open_qty'}
                                                required={activeFields.sizeGroup === 'open_qty'}
                                            />
                                        </div>
                                    </div>
                                )}

                                {(formData.base_mrk !== 'fixprice' && formData.base_mrk !== 'spot') ? (
                                    <div className="form-group-radio">
                                        <div className="radio-group">
                                            <div className="radio-option">
                                                <input
                                                    type="radio"
                                                    id="incr_lvrg"
                                                    name="leverageGroup"
                                                    checked={activeFields.leverageGroup === 'incr_lvrg'}
                                                    onChange={() => handleRadioChange('leverageGroup', 'incr_lvrg')}
                                                />
                                                <label htmlFor="incr_lvrg">
                                                    {t('hedge.incrementalLeverage')}
                                                    <InfoTooltip text={t('hedge.tooltips.incrementalLeverage')} />
                                                </label>
                                            </div>
                                            <input
                                                type="number"
                                                id="incr_lvrg"
                                                name="incr_lvrg"
                                                value={formData.incr_lvrg}
                                                onChange={handleInputChange}
                                                disabled={activeFields.leverageGroup !== 'incr_lvrg'}
                                                required={activeFields.leverageGroup === 'incr_lvrg'}
                                            />
                                        </div>

                                        <div className="radio-group">
                                            <div className="radio-option">
                                                <input
                                                    type="radio"
                                                    id="fix_lvrg"
                                                    name="leverageGroup"
                                                    checked={activeFields.leverageGroup === 'fix_lvrg'}
                                                    onChange={() => handleRadioChange('leverageGroup', 'fix_lvrg')}
                                                />
                                                <label htmlFor="fix_lvrg">
                                                    {t('hedge.fixedLeverage')}
                                                    <InfoTooltip text={t('hedge.tooltips.fixedLeverage')} />
                                                </label>
                                            </div>
                                            <input
                                                type="number"
                                                id="fix_lvrg"
                                                name="fix_lvrg"
                                                value={formData.fix_lvrg}
                                                onChange={handleInputChange}
                                                disabled={activeFields.leverageGroup !== 'fix_lvrg'}
                                                required={activeFields.leverageGroup === 'fix_lvrg'}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="form-group">
                                        <label htmlFor="fix_lvrg">
                                            {t('hedge.fixedLeverage')}
                                            <InfoTooltip text={t('hedge.tooltips.fixedLeverage')} />
                                        </label>
                                        <input
                                            type="number"
                                            id="fix_lvrg"
                                            name="fix_lvrg"
                                            value={formData.fix_lvrg}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                )}

                                {formData.base_mrk !== 'fixprice' ? (
                                    <div className="form-group-radio">
                                        <div className="radio-group">
                                            <div className="radio-option">
                                                <input
                                                    type="radio"
                                                    id="open_perc"
                                                    name="openGroup"
                                                    checked={activeFields.openGroup === 'open_perc'}
                                                    onChange={() => handleRadioChange('openGroup', 'open_perc')}
                                                />
                                                <label htmlFor="open_perc">
                                                    {t('hedge.openPercentage')}
                                                    <InfoTooltip text={t('hedge.tooltips.openPercentage')} />
                                                </label>
                                            </div>
                                            <input
                                                type="number"
                                                id="open_perc"
                                                name="open_perc"
                                                value={formData.open_perc}
                                                onChange={handleInputChange}
                                                step="any"
                                                disabled={activeFields.openGroup !== 'open_perc'}
                                                required={activeFields.openGroup === 'open_perc'}
                                            />
                                        </div>

                                        <div className="radio-group">
                                            <div className="radio-option">
                                                <input
                                                    type="radio"
                                                    id="open_price"
                                                    name="openGroup"
                                                    checked={activeFields.openGroup === 'open_price'}
                                                    onChange={() => handleRadioChange('openGroup', 'open_price')}
                                                />
                                                <label htmlFor="open_price">
                                                    {t('hedge.openPrice')}
                                                    <InfoTooltip text={t('hedge.tooltips.openPrice')} />
                                                </label>
                                            </div>
                                            <input
                                                type="number"
                                                id="open_price"
                                                name="open_price"
                                                value={formData.open_price}
                                                onChange={handleInputChange}
                                                step="any"
                                                disabled={activeFields.openGroup !== 'open_price'}
                                                required={activeFields.openGroup === 'open_price'}
                                            />
                                        </div>

                                        <div className="radio-group">
                                            <div className="radio-option">
                                                <input
                                                    type="radio"
                                                    id="open_dd"
                                                    name="openGroup"
                                                    checked={activeFields.openGroup === 'open_dd'}
                                                    onChange={() => handleRadioChange('openGroup', 'open_dd')}
                                                />
                                                <label htmlFor="open_dd">
                                                    {t('hedge.openDrawdown')}
                                                    <InfoTooltip text={t('hedge.tooltips.openDrawdown')} />
                                                </label>
                                            </div>
                                            <input
                                                type="number"
                                                id="open_dd"
                                                name="open_dd"
                                                value={formData.open_dd}
                                                onChange={handleInputChange}
                                                step="any"
                                                min="0"
                                                disabled={activeFields.openGroup !== 'open_dd'}
                                                required={activeFields.openGroup === 'open_dd'}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="form-group">
                                        <label htmlFor="open_price">
                                            {t('hedge.openPrice')}
                                            <InfoTooltip text={t('hedge.tooltips.openPrice')} />
                                        </label>
                                        <input
                                            type="number"
                                            id="open_price"
                                            name="open_price"
                                            value={formData.open_price}
                                            onChange={handleInputChange}
                                            step="any"
                                            required
                                        />
                                    </div>
                                )}

                                <div className="form-group">
                                    <label htmlFor="buff_perc">
                                        {t('hedge.bufferPercentage')}
                                        <InfoTooltip text={t('hedge.tooltips.bufferPercentage')} />
                                    </label>
                                    <input
                                        type="number"
                                        id="buff_perc"
                                        name="buff_perc"
                                        value={formData.buff_perc}
                                        onChange={handleInputChange}
                                        step="any"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="safe_zone_mode">
                                        {t('hedge.bufferZoneMode')}
                                        <InfoTooltip text={t('hedge.tooltips.bufferZoneMode')} />
                                    </label>
                                    <CustomSelect
                                        id="safe_zone_mode"
                                        name="safe_zone_mode"
                                        value={formData.safe_zone_mode}
                                        onChange={handleInputChange}
                                        required
                                        options={[
                                            { value: 'follow', label: t('hedge.safeZoneModeFollow') },
                                            { value: 'expand', label: t('hedge.safeZoneModeExpand') }
                                        ]}
                                    />
                                </div>

                                {formData.base_mrk !== 'fixprice' && (
                                    <div className="form-group">
                                        <label htmlFor="hedge_tp_move_base">
                                            {t('hedge.hedgeTpMoveBase')}
                                            <InfoTooltip text={t('hedge.tooltips.hedgeTpMoveBase')} />
                                        </label>
                                        <CustomSelect
                                            id="hedge_tp_move_base"
                                            name="hedge_tp_move_base"
                                            value={formData.hedge_tp_move_base}
                                            onChange={handleInputChange}
                                            required
                                            options={[
                                                { value: '0', label: t('common.no') },
                                                { value: '1', label: t('common.yes') }
                                            ]}
                                        />
                                    </div>
                                )}

                                <div className="form-group">
                                    <label htmlFor="open_mode">
                                        {t('hedge.openMode')}
                                        <InfoTooltip text={t('hedge.tooltips.openMode')} />
                                    </label>
                                    <CustomSelect
                                        id="open_mode"
                                        name="open_mode"
                                        value={formData.open_mode}
                                        onChange={handleInputChange}
                                        required
                                        options={[
                                            { value: 'market', label: t('hedge.openModeMarket') },
                                            { value: 'limit', label: t('hedge.openModeLimit') }
                                        ]}
                                    />
                                </div>

                                {/*<div className="form-group">*/}
                                {/*    <label htmlFor="close_mode">Режим закрытия позиций</label>*/}
                                {/*    <select*/}
                                {/*        id="close_mode"*/}
                                {/*        name="close_mode"*/}
                                {/*        value={formData.close_mode}*/}
                                {/*        onChange={handleInputChange}*/}
                                {/*        required*/}
                                {/*    >*/}
                                {/*        <option value="market">Market</option>*/}
                                {/*        <option value="limit">Limit</option>*/}
                                {/*    </select>*/}
                                {/*</div>*/}
                            </div>


                            <div className="form-section">
                                <h4>{t('hedge.closeSettings')}</h4>

                                <div className="form-group">
                                    <label htmlFor="be_act_perc">
                                        {t('hedge.breakEvenActivationPercentage')}
                                        <InfoTooltip text={t('hedge.tooltips.breakEvenActivationPercentage')} />
                                    </label>
                                    <input
                                        type="number"
                                        id="be_act_perc"
                                        name="be_act_perc"
                                        value={formData.be_act_perc}
                                        onChange={handleInputChange}
                                        step="any"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="be_perc">
                                        {t('hedge.breakEvenPercentage')}
                                        <InfoTooltip text={t('hedge.tooltips.breakEvenPercentage')} />
                                    </label>
                                    <input
                                        type="number"
                                        id="be_perc"
                                        name="be_perc"
                                        value={formData.be_perc}
                                        onChange={handleInputChange}
                                        step="any"
                                        required
                                    />
                                </div>

                                {formData.base_mrk !== 'fixprice' && (
                                    <div className="form-group">
                                        <label htmlFor="hedge_tp_perc">
                                            {t('hedge.hedgeTakeProfit')}
                                            <InfoTooltip text={t('hedge.tooltips.hedgeTakeProfit')} />
                                        </label>
                                        <input
                                            type="number"
                                            id="hedge_tp_perc"
                                            name="hedge_tp_perc"
                                            value={formData.hedge_tp_perc}
                                            onChange={handleInputChange}
                                            step="any"
                                            required
                                        />
                                    </div>
                                )}

                                {formData.base_mrk !== 'fixprice' && (
                                    <div className="form-group">
                                        <label htmlFor="close_after_hours">
                                            {t('hedge.closeAfterHours')}
                                            <InfoTooltip text={t('hedge.tooltips.closeAfterHours')} />
                                        </label>
                                        <input
                                            type="number"
                                            id="close_after_hours"
                                            name="close_after_hours"
                                            value={formData.close_after_hours}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                )}

                                <div className="form-group">
                                    <label htmlFor="min_opened_time">
                                        {t('hedge.minOpenedTime')}
                                        <InfoTooltip text={t('hedge.tooltips.minOpenedTime')} />
                                    </label>
                                    <input
                                        type="number"
                                        id="min_opened_time"
                                        name="min_opened_time"
                                        value={formData.min_opened_time}
                                        onChange={handleInputChange}
                                        min="0"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="check_close_interval">
                                        {t('hedge.checkCloseInterval')}
                                        <InfoTooltip text={t('hedge.tooltips.checkCloseInterval')} />
                                    </label>
                                    <input
                                        type="number"
                                        id="check_close_interval"
                                        name="check_close_interval"
                                        value={formData.check_close_interval}
                                        onChange={handleInputChange}
                                        min="0"
                                        placeholder={t('hedge.checkCloseIntervalPlaceholder')}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="only_one_be">
                                        {t('hedge.onlyOneBreakEven')}
                                        <InfoTooltip text={t('hedge.tooltips.onlyOneBreakEven')} />
                                    </label>
                                    <CustomSelect
                                        id="only_one_be"
                                        name="only_one_be"
                                        value={formData.only_one_be}
                                        onChange={handleInputChange}
                                        required
                                        options={[
                                            { value: '0', label: t('hedge.onlyOneBreakEvenNo') },
                                            { value: '1', label: t('hedge.onlyOneBreakEvenYes') }
                                        ]}
                                    />
                                </div>

                                {/* Force Close fields hidden - managed via action buttons in config list */}
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="submit-button">
                                {editingConfig ? t('hedge.saveChanges') : t('hedge.addConfig')}
                            </button>
                        </div>
                    </form>
                    )}
                </div>
            )}

            {/* Список конфигураций */}
            {loading ? (
                <div className="loading">{t('common.loading')}</div>
            ) : configs.length === 0 ? (
                <div className="no-data">{t('hedge.noConfigs')}</div>
            ) : filteredConfigs.length === 0 ? (
                <div className="no-data">{t('hedge.noFilteredConfigs')}</div>
            ) : (
                !(showAddForm || editingConfig) && (
                    <div className="configs-table-container">
                        <table className="configs-table">
                            <thead>
                            <tr>
                                <th className="sortable" onClick={() => handleSort('status')}>
                                    {t('common.status')}
                                    {sortField === 'status' && (
                                        <span className="sort-indicator">{sortDirection === 'asc' ? ' ▲' : ' ▼'}</span>
                                    )}
                                </th>
                                <th className="sortable" onClick={() => handleSort('name')}>
                                    {t('hedge.configName')}
                                    {sortField === 'name' && (
                                        <span className="sort-indicator">{sortDirection === 'asc' ? ' ▲' : ' ▼'}</span>
                                    )}
                                </th>
                                <th className="sortable" onClick={() => handleSort('asset')}>
                                    {t('hedge.baseSymbolFilter')}
                                    {sortField === 'asset' && (
                                        <span className="sort-indicator">{sortDirection === 'asc' ? ' ▲' : ' ▼'}</span>
                                    )}
                                </th>
                                <th className="sortable" onClick={() => handleSort('market')}>
                                    {t('hedge.mainMarket')}
                                    {sortField === 'market' && (
                                        <span className="sort-indicator">{sortDirection === 'asc' ? ' ▲' : ' ▼'}</span>
                                    )}
                                </th>
                                <th className="sortable" onClick={() => handleSort('connection')}>
                                    {t('hedge.hedgeConnectionTitle')}
                                    {sortField === 'connection' && (
                                        <span className="sort-indicator">{sortDirection === 'asc' ? ' ▲' : ' ▼'}</span>
                                    )}
                                </th>
                                <th className="sortable" onClick={() => handleSort('connectionStatus')}>
                                    {t('hedge.connectionStatus')}
                                    {sortField === 'connectionStatus' && (
                                        <span className="sort-indicator">{sortDirection === 'asc' ? ' ▲' : ' ▼'}</span>
                                    )}
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            {sortConfigs(filteredConfigs).map(config => (
                                <React.Fragment key={config.id}>
                                    {/* Main data row */}
                                    <tr className={`config-row ${config.active === 1 ? 'active' : 'inactive'}`}
                                        onClick={() => startEditing(config)}
                                    >
                                        <td data-label={t('common.status')}>
                                            <span
                                                className={`status-indicator ${config.active === 1 ? 'active' : 'inactive'}`}>
                                                {config.active === 1 ? '🟢' : '🔴'}
                                            </span>
                                        </td>
                                        <td data-label={t('hedge.configName')}>ID: {config.id} |
                                            ({config.conf_descr || t('hedge.noTitle')})
                                        </td>
                                        <td data-label={t('hedge.pair')}>{config.base_symbol} → {config.hedge_symbol}</td>
                                        <td data-label={t('hedge.mainMarket')}>{getMarketName(config.base_mrk)}</td>
                                        <td data-label={t('hedge.hedgeConnectionTitle')}>ID: {config.base_conn_id} | {getConnectionName(config.base_conn_id)} |
                                            {t('hedge.exchange')}: {getExchangeName(config.base_conn_id)}</td>
                                        <td data-label={t('hedge.connectionStatus')}>
                                            <span
                                                className={`connection-status-indicator ${isConnectionActive(config.base_conn_id) ? 'active' : 'inactive'}`}>
                                                {isConnectionActive(config.base_conn_id) ? `🟢 ${t('hedge.connectionActive')}` : `🔴 ${t('hedge.connectionInactive')}`}
                                            </span>
                                        </td>
                                    </tr>
                                    {/* Actions row */}
                                    <tr className="actions-row">
                                        <td colSpan="6">
                                            <div className="inline-actions">
                                                <button
                                                    className="action-btn edit"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        startEditing(config);
                                                    }}
                                                    title={t('common.edit')}
                                                >
                                                    <Edit size={14} />
                                                    {t('common.edit')}
                                                </button>
                                                <button
                                                    className="action-btn duplicate"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDuplicateConfig(config);
                                                    }}
                                                    title={t('hedge.duplicateConfig')}
                                                >
                                                    <Copy size={14} />
                                                    {t('hedge.duplicateConfig')}
                                                </button>
                                                <button
                                                    className={`action-btn ${config.active === 1 ? 'deactivate' : 'activate'}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleToggleActive(config.id, config.active);
                                                    }}
                                                    title={config.active === 1 ? t('hedge.deactivate') : t('hedge.activate')}
                                                >
                                                    {config.active === 1 ? <Pause size={14} /> : <Play size={14} />}
                                                    {config.active === 1 ? t('hedge.deactivate') : t('hedge.activate')}
                                                </button>
                                                {config.active === 1 && (
                                                    <>
                                                        <button
                                                            className="action-btn force-close-hedge"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleForceCloseHedge(config);
                                                            }}
                                                            title={t('hedge.forceCloseHedge')}
                                                        >
                                                            <XCircle size={14} />
                                                            {t('hedge.forceCloseHedge')}
                                                        </button>
                                                        {config.base_mrk !== 'fixprice' && (
                                                            <button
                                                                className="action-btn force-close-all"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleForceCloseAll(config);
                                                                }}
                                                                title={t('hedge.forceCloseAll')}
                                                            >
                                                                <XCircle size={14} />
                                                                {t('hedge.forceCloseAll')}
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                                <button
                                                    className="action-btn delete"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteConfig(config.id);
                                                    }}
                                                    title={t('common.delete')}
                                                >
                                                    <Trash2 size={14} />
                                                    {t('common.delete')}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )
            )}

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                title={confirmDialog.title}
                message={confirmDialog.message}
                variant={confirmDialog.variant}
                confirmText={confirmDialog.confirmText}
                cancelText={confirmDialog.cancelText}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
            />
        </div>
    );
};

export default HedgeConfigsList;
